/**
 * Firestore Service
 * - iOS: 使用 Capacitor 原生 API（效能較好，已上架正常運作）
 * - Android/Web: 使用 Web SDK（Android 原生 API 有超時問題）
 */

import { db } from '../firebase'
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp as webServerTimestamp,
  type DocumentData,
} from 'firebase/firestore'
import { FirebaseFirestore } from '@capacitor-firebase/firestore'

// 判斷平台
const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor !== undefined
const platform = isCapacitor ? (window as any).Capacitor?.getPlatform?.() : 'web'

// 只在 iOS 上使用原生 API，Android 直接用 Web SDK（原生 API 有超時問題）
const useNativeFirestore = isCapacitor && platform === 'ios'

// 調試日誌
console.log('[FirestoreService] 平台:', platform, '使用原生 Firestore:', useNativeFirestore)

/**
 * 獲取集合中的所有文檔
 */
export async function getCollection<T = DocumentData>(path: string): Promise<Array<{ id: string } & T>> {
  if (useNativeFirestore) {
    // iOS: 使用原生 Firestore API
    try {
      const result = await FirebaseFirestore.getCollection({
        reference: path,
      })
      return result.snapshots.map((snap: { id: string; data: unknown }) => ({
        id: snap.id,
        ...(snap.data as T),
      }))
    } catch (error) {
      console.error('Capacitor getCollection 失敗:', error)
      throw error
    }
  } else {
    // Android/Web: 使用 Web SDK
    const ref = collection(db, path)
    const snap = await getDocs(ref)
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as T),
    }))
  }
}

/**
 * 獲取單個文檔
 */
export async function getDocument<T = DocumentData>(path: string): Promise<({ id: string } & T) | null> {
  if (useNativeFirestore) {
    // iOS: 使用原生 Firestore API
    try {
      const result = await FirebaseFirestore.getDocument({
        reference: path,
      })

      if (result.snapshot && result.snapshot.data !== null && result.snapshot.data !== undefined) {
        return {
          id: result.snapshot.id,
          ...(result.snapshot.data as T),
        }
      }
      return null
    } catch (error) {
      throw error
    }
  } else {
    // Android/Web: 使用 Web SDK
    const docRef = doc(db, path)
    const snap = await getDoc(docRef)
    if (snap.exists()) {
      return {
        id: snap.id,
        ...(snap.data() as T),
      }
    }
    return null
  }
}

/**
 * 設置文檔（如果不存在則創建，存在則覆蓋）
 */
export async function setDocument(path: string, data: DocumentData): Promise<void> {
  console.log('[FirestoreService] setDocument 開始:', path)
  console.log('[FirestoreService] 資料:', JSON.stringify(data, null, 2))

  if (useNativeFirestore) {
    // iOS: 使用原生 Firestore API
    try {
      // Capacitor Firestore 可能需要明確的資料格式
      // 確保所有值都是可序列化的
      const cleanData = JSON.parse(JSON.stringify(data))

      await FirebaseFirestore.setDocument({
        reference: path,
        data: cleanData,
      })
      console.log('[FirestoreService] iOS 原生 setDocument 成功:', path)
    } catch (error) {
      console.error('[FirestoreService] iOS 原生 setDocument 失敗:', path, error)
      throw error
    }
  } else {
    // Android/Web: 使用 Web SDK
    try {
      console.log('[FirestoreService] 使用 Web SDK setDoc...')
      const docRef = doc(db, path)
      console.log('[FirestoreService] docRef 建立完成')
      await setDoc(docRef, data)
      console.log('[FirestoreService] Web SDK setDoc 成功:', path)
    } catch (error: any) {
      console.error('[FirestoreService] Web SDK setDoc 失敗:', path)
      console.error('[FirestoreService] 錯誤詳情:', {
        message: error?.message,
        code: error?.code,
        name: error?.name,
        stack: error?.stack
      })
      throw error
    }
  }
}

/**
 * 合併寫入文檔（set with merge: true）
 * 文檔不存在時建立；存在時只更新傳入的欄位，不影響其他欄位
 */
export async function mergeDocument(path: string, data: DocumentData): Promise<void> {
  if (useNativeFirestore) {
    const cleanData = JSON.parse(JSON.stringify(data))
    await FirebaseFirestore.setDocument({ reference: path, data: cleanData, merge: true })
  } else {
    await setDoc(doc(db, path), data, { merge: true })
  }
}

/**
 * 更新文檔
 */
export async function updateDocument(path: string, data: Partial<DocumentData>): Promise<void> {
  if (useNativeFirestore) {
    // iOS: 使用原生 Firestore API
    try {
      await FirebaseFirestore.updateDocument({
        reference: path,
        data: data,
      })
    } catch (error) {
      throw error
    }
  } else {
    // Android/Web: 使用 Web SDK
    const docRef = doc(db, path)
    await updateDoc(docRef, data)
  }
}

/**
 * 刪除文檔
 */
export async function deleteDocument(path: string): Promise<void> {
  if (useNativeFirestore) {
    // iOS: 使用原生 Firestore API
    try {
      await FirebaseFirestore.deleteDocument({
        reference: path,
      })
    } catch (error) {
      throw error
    }
  } else {
    // Android/Web: 使用 Web SDK
    const docRef = doc(db, path)
    await deleteDoc(docRef)
  }
}

/**
 * 服務器時間戳
 */
export function serverTimestamp() {
  if (useNativeFirestore) {
    // iOS: 在 Capacitor 中，直接使用當前時間的 ISO 字符串
    return new Date().toISOString()
  } else {
    // Android/Web: 使用 Firestore 的服務器時間戳
    return webServerTimestamp()
  }
}
